/**
 * validation.ts — pure diagnostics validation rule engine.
 *
 * Turns a `DiagnosticsSnapshot` (native-diagnostics-spec) into a
 * `DiagnosticsValidationReport` of per-module pass/warn/fail checks, using the
 * thresholds in `native-diagnostics-validation` §5 and the overall-severity
 * synthesis in §4.
 *
 * The engine is a pure function — no clock, no IO — so it runs identically in the
 * dev panel, vitest, and CI. The caller supplies `generatedAtMs` (defaults to the
 * snapshot timestamp) to keep it deterministic.
 */

import { DIAGNOSTICS_THRESHOLDS, THRESHOLD_VERSION } from './thresholds';

import type { DiagnosticsSnapshot, ThermalState } from '../types';

// ──────────────────────────────────────────────────────────────────────────
// Result model (native-diagnostics-validation §4)
// ──────────────────────────────────────────────────────────────────────────

export type DiagnosticSeverity = 'pass' | 'warn' | 'fail';

export type DiagnosticModule =
  | 'session'
  | 'audio_input'
  | 'ring_buffer'
  | 'analysis'
  | 'detector'
  | 'post_processor'
  | 'event_dispatcher'
  | 'ui_bridge'
  | 'degradation'
  | 'platform';

export type DiagnosticCheckResult = {
  id: string;
  module: DiagnosticModule;
  severity: DiagnosticSeverity;
  observed: number | string | boolean | null;
  threshold?: string;
  basis: string;
  message: string;
  timestampMs: number;
};

export type DiagnosticsValidationReport = {
  schemaVersion: 1;
  thresholdVersion: string;
  generatedAtMs: number;
  sessionId: string | null;
  device?: {
    platform: 'ios' | 'android';
    model?: string;
    osVersion?: string;
  };
  snapshotTimestampMs: number;
  overall: DiagnosticSeverity;
  checks: DiagnosticCheckResult[];
};

export type ValidateDiagnosticsOptions = {
  /** Defaults to the snapshot timestamp to keep the engine deterministic. */
  generatedAtMs?: number;
  device?: DiagnosticsValidationReport['device'];
};

// ──────────────────────────────────────────────────────────────────────────
// Severity helpers
// ──────────────────────────────────────────────────────────────────────────

const RANK: Record<DiagnosticSeverity, number> = { pass: 0, warn: 1, fail: 2 };

/** Synthesise overall severity: any fail → fail, else any warn → warn (§4). */
const worst = (severities: DiagnosticSeverity[]): DiagnosticSeverity =>
  severities.reduce<DiagnosticSeverity>(
    (acc, s) => (RANK[s] > RANK[acc] ? s : acc),
    'pass',
  );

/** Ascending bands — higher value is worse. `null`/non-finite ⇒ `unknown`. */
const ascending = (
  value: number | null,
  warnAt: number,
  failAt: number,
  unknown: DiagnosticSeverity = 'pass',
): DiagnosticSeverity => {
  if (value == null || !Number.isFinite(value)) return unknown;
  if (value >= failAt) return 'fail';
  if (value >= warnAt) return 'warn';
  return 'pass';
};

const ratio = (numerator: number, denominator: number | null): number | null =>
  denominator == null || denominator <= 0 ? null : numerator / denominator;

// ──────────────────────────────────────────────────────────────────────────
// Per-module rule builders — each returns DiagnosticCheckResult[]
// ──────────────────────────────────────────────────────────────────────────

type Check = Omit<DiagnosticCheckResult, 'timestampMs'>;

const buildSession = (s: DiagnosticsSnapshot): Check[] => {
  const { session } = s;
  const stateSeverity: DiagnosticSeverity =
    session.state === 'running'
      ? 'pass'
      : session.state === 'error' || session.state === 'stopped'
        ? 'fail'
        : 'warn';
  return [
    {
      id: 'session.state.running',
      module: 'session',
      severity: stateSeverity,
      observed: session.state,
      threshold: 'running',
      basis: 'session state gates every downstream module',
      message: `session state is ${session.state}`,
    },
    {
      id: 'session.error.none',
      module: 'session',
      severity: session.lastErrorCode ? 'warn' : 'pass',
      observed: session.lastErrorCode ?? null,
      basis: 'a reported error code may indicate a recoverable failure',
      message: session.lastErrorCode
        ? `last error: ${session.lastErrorCode}`
        : 'no error reported',
    },
    {
      id: 'session.uptime.valid',
      module: 'session',
      severity: session.uptimeMs > 0 ? 'pass' : 'warn',
      observed: session.uptimeMs,
      threshold: '> 0ms',
      basis: 'uptime should advance while the session clock is healthy',
      message: `uptime ${session.uptimeMs}ms`,
    },
  ];
};

const buildAudioInput = (s: DiagnosticsSnapshot): Check[] => {
  const a = s.audioInput;
  const bufferMs = ratio(
    (a.inputBufferSizeFrames ?? 0) * 1000,
    a.actualSampleRate,
  );
  const { callbackBudget, callbackAge, supportedSampleRates } =
    DIAGNOSTICS_THRESHOLDS;
  const permissionSeverity: DiagnosticSeverity =
    a.permissionState === 'granted'
      ? 'pass'
      : a.permissionState === 'denied'
        ? 'fail'
        : 'warn';
  const rateSeverity: DiagnosticSeverity =
    a.actualSampleRate == null
      ? 'fail'
      : (supportedSampleRates as readonly number[]).includes(a.actualSampleRate)
        ? 'pass'
        : 'warn';
  return [
    {
      id: 'audio.permission',
      module: 'audio_input',
      severity: permissionSeverity,
      observed: a.permissionState,
      threshold: 'granted',
      basis: 'mic permission gates input start',
      message: `permission ${a.permissionState}`,
    },
    {
      id: 'audio.callback.budget',
      module: 'audio_input',
      severity: ascending(
        ratio(a.callbackAvgMs ?? NaN, bufferMs),
        callbackBudget.warn,
        callbackBudget.fail,
      ),
      observed: a.callbackAvgMs,
      threshold: `< ${callbackBudget.warn * 100}% buffer`,
      basis: 'long callbacks risk missing the next buffer (dropout)',
      message: `callback avg ${a.callbackAvgMs ?? '—'}ms`,
    },
    {
      id: 'audio.callback.alive',
      module: 'audio_input',
      severity: ascending(
        ratio(a.lastCallbackAgeMs ?? NaN, bufferMs),
        callbackAge.warn,
        callbackAge.fail,
        'warn',
      ),
      observed: a.lastCallbackAgeMs,
      threshold: `< ${callbackAge.warn}x buffer`,
      basis: 'callback age is the most direct liveness signal',
      message: `last callback age ${a.lastCallbackAgeMs ?? '—'}ms`,
    },
    {
      id: 'audio.dropout.count',
      module: 'audio_input',
      severity: a.inputDropoutCount > 0 ? 'warn' : 'pass',
      observed: a.inputDropoutCount,
      threshold: '0',
      basis: 'any input dropout is real audio loss',
      message: `input dropouts ${a.inputDropoutCount}`,
    },
    {
      id: 'audio.sample_rate.supported',
      module: 'audio_input',
      severity: rateSeverity,
      observed: a.actualSampleRate,
      threshold: '44100/48000',
      basis: 'sample rate affects analysis params and timestamp accuracy',
      message: `sample rate ${a.actualSampleRate ?? 'null'}`,
    },
  ];
};

const buildRingBuffer = (s: DiagnosticsSnapshot): Check[] => {
  const r = s.ringBuffer;
  const { ringBacklogMs, ringFillRatio } = DIAGNOSTICS_THRESHOLDS;
  return [
    {
      id: 'ring.backlog',
      module: 'ring_buffer',
      severity: ascending(r.backlogMs, ringBacklogMs.warn, ringBacklogMs.fail),
      observed: r.backlogMs,
      threshold: `< ${ringBacklogMs.warn}ms`,
      basis: 'backlog above 250ms is perceptible; above 500ms prefer resync',
      message: `backlog ${r.backlogMs}ms`,
    },
    {
      id: 'ring.fill_ratio',
      module: 'ring_buffer',
      severity: ascending(r.fillRatio, ringFillRatio.warn, ringFillRatio.fail),
      observed: r.fillRatio,
      threshold: `< ${ringFillRatio.warn}`,
      basis: 'high fill ratio means overrun is imminent',
      message: `fill ratio ${r.fillRatio}`,
    },
    {
      id: 'ring.overrun',
      module: 'ring_buffer',
      severity: r.overrunCount > 0 ? 'warn' : 'pass',
      observed: r.overrunCount,
      threshold: '0',
      basis: 'overrun signals producer/consumer imbalance',
      message: `overruns ${r.overrunCount}`,
    },
    {
      id: 'ring.underrun',
      module: 'ring_buffer',
      severity: r.underrunCount > 0 ? 'warn' : 'pass',
      observed: r.underrunCount,
      threshold: '0',
      basis: 'underrun means the worker is starved of samples',
      message: `underruns ${r.underrunCount}`,
    },
  ];
};

const buildAnalysis = (s: DiagnosticsSnapshot): Check[] => {
  const a = s.analysis;
  const hopMs = ratio(a.hopSize * 1000, a.analysisSampleRate);
  const { analysisAvg, analysisP95, analysisFrameAge } = DIAGNOSTICS_THRESHOLDS;
  const stateSeverity: DiagnosticSeverity =
    a.workerState === 'error' || a.workerState === 'stopped'
      ? 'fail'
      : a.workerState === 'overloaded'
        ? 'warn'
        : 'pass';
  return [
    {
      id: 'analysis.state',
      module: 'analysis',
      severity: stateSeverity,
      observed: a.workerState,
      threshold: 'running',
      basis: 'analysis pipeline liveness',
      message: `worker ${a.workerState}`,
    },
    {
      id: 'analysis.avg_time',
      module: 'analysis',
      severity: ascending(
        ratio(a.avgProcessMs ?? NaN, hopMs),
        analysisAvg.warn,
        analysisAvg.fail,
      ),
      observed: a.avgProcessMs,
      threshold: `< ${analysisAvg.warn * 100}% hop`,
      basis: 'steady-state headroom against the frame deadline',
      message: `avg ${a.avgProcessMs ?? '—'}ms`,
    },
    {
      id: 'analysis.p95_time',
      module: 'analysis',
      severity: ascending(
        ratio(a.p95ProcessMs ?? NaN, hopMs),
        analysisP95.warn,
        analysisP95.fail,
      ),
      observed: a.p95ProcessMs,
      threshold: `< ${analysisP95.warn * 100}% hop`,
      basis: 'p95 reveals spike tolerance the average hides',
      message: `p95 ${a.p95ProcessMs ?? '—'}ms`,
    },
    {
      id: 'analysis.deadline',
      module: 'analysis',
      severity: a.deadlineMissCount > 0 ? 'warn' : 'pass',
      observed: a.deadlineMissCount,
      threshold: '0',
      basis: 'deadline miss causes frame skip / backlog',
      message: `deadline misses ${a.deadlineMissCount}`,
    },
    {
      id: 'analysis.last_frame_age',
      module: 'analysis',
      severity: ascending(
        ratio(a.lastFrameAgeMs ?? NaN, hopMs),
        analysisFrameAge.warn,
        analysisFrameAge.fail,
        'warn',
      ),
      observed: a.lastFrameAgeMs,
      threshold: `< ${analysisFrameAge.warn}x hop`,
      basis: 'stale last frame means the worker stalled or is overloaded',
      message: `last frame age ${a.lastFrameAgeMs ?? '—'}ms`,
    },
    {
      id: 'analysis.frame_skip',
      module: 'analysis',
      severity: a.frameSkipCount > 0 ? 'warn' : 'pass',
      observed: a.frameSkipCount,
      threshold: '0',
      basis: 'frame skip is a degradation-level candidate',
      message: `frame skips ${a.frameSkipCount}`,
    },
  ];
};

const buildDetector = (s: DiagnosticsSnapshot): Check[] => {
  const d = s.detector;
  const { detectorConfidenceWarn } = DIAGNOSTICS_THRESHOLDS;
  const confidenceSeverity: DiagnosticSeverity =
    d.avgConfidence == null
      ? 'pass'
      : d.avgConfidence === 0
        ? 'fail'
        : d.avgConfidence < detectorConfidenceWarn
          ? 'warn'
          : 'pass';
  return [
    {
      id: 'detector.active',
      module: 'detector',
      severity: d.active ? 'pass' : 'fail',
      observed: d.active,
      threshold: 'true',
      basis: 'F0 analysis requires an active detector',
      message: d.active ? 'detector active' : 'detector inactive',
    },
    {
      id: 'detector.algorithm',
      module: 'detector',
      severity: d.algorithm === 'unknown' ? 'fail' : 'pass',
      observed: d.algorithm,
      threshold: 'mpm/yin/crepe/hybrid',
      basis: 'unknown algorithm signals an engine factory / config fault',
      message: `algorithm ${d.algorithm}`,
    },
    {
      id: 'detector.confidence',
      module: 'detector',
      severity: confidenceSeverity,
      observed: d.avgConfidence,
      threshold: `>= ${detectorConfidenceWarn}`,
      basis: 'persistently zero/low confidence signals input/threshold issues',
      message: `avg confidence ${d.avgConfidence ?? '—'}`,
    },
  ];
};

const buildPostProcessor = (s: DiagnosticsSnapshot): Check[] => {
  const p = s.postProcessor;
  const frames = s.analysis.frameCount;
  const { postHoldRate, postInterpolationRate } = DIAGNOSTICS_THRESHOLDS;
  return [
    {
      id: 'post.hold_rate',
      module: 'post_processor',
      severity: ascending(
        ratio(p.heldFrameCount, frames || null),
        postHoldRate.warn,
        postHoldRate.fail,
      ),
      observed: p.heldFrameCount,
      threshold: `< ${postHoldRate.warn} of frames`,
      basis: 'over-holding hides detector dropout from the UI',
      message: `held ${p.heldFrameCount}/${frames}`,
    },
    {
      id: 'post.interpolation_rate',
      module: 'post_processor',
      severity: ascending(
        ratio(p.interpolatedFrameCount, frames || null),
        postInterpolationRate.warn,
        postInterpolationRate.fail,
      ),
      observed: p.interpolatedFrameCount,
      threshold: `< ${postInterpolationRate.warn} of frames`,
      basis: 'over-interpolation masks real signal loss',
      message: `interpolated ${p.interpolatedFrameCount}/${frames}`,
    },
  ];
};

const buildEventDispatcher = (s: DiagnosticsSnapshot): Check[] => {
  const e = s.eventDispatcher;
  const { eventQueueDepth, eventDropRate, eventLastAge } =
    DIAGNOSTICS_THRESHOLDS;
  const emittedTotal = e.emittedPitchFrameCount + e.droppedPitchFrameCount;
  return [
    {
      id: 'event.queue_depth',
      module: 'event_dispatcher',
      severity: ascending(
        e.eventQueueDepth,
        eventQueueDepth.warn,
        eventQueueDepth.fail,
      ),
      observed: e.eventQueueDepth,
      threshold: `< ${eventQueueDepth.warn}`,
      basis: 'rising queue depth signals slow JS consumption or event flood',
      message: `queue depth ${e.eventQueueDepth}`,
    },
    {
      id: 'event.drop_rate',
      module: 'event_dispatcher',
      severity: ascending(
        ratio(e.droppedPitchFrameCount, emittedTotal || null),
        eventDropRate.warn,
        eventDropRate.fail,
      ),
      observed: e.droppedPitchFrameCount,
      threshold: `< ${eventDropRate.warn}`,
      basis: 'high pitch-frame drop rate means event backpressure',
      message: `dropped ${e.droppedPitchFrameCount}/${emittedTotal}`,
    },
    {
      id: 'event.last_age',
      module: 'event_dispatcher',
      severity: ascending(
        ratio(e.lastEventAgeMs ?? NaN, e.pitchEventIntervalMs || null),
        eventLastAge.warn,
        eventLastAge.fail,
        'warn',
      ),
      observed: e.lastEventAgeMs,
      threshold: `< ${eventLastAge.warn}x interval`,
      basis: 'a stale last event means the dispatcher stalled',
      message: `last event age ${e.lastEventAgeMs ?? '—'}ms`,
    },
  ];
};

const buildUiBridge = (s: DiagnosticsSnapshot): Check[] => {
  const u = s.uiBridge;
  const { uiConsumerLagMs } = DIAGNOSTICS_THRESHOLDS;
  return [
    {
      id: 'ui.snapshot_stale',
      module: 'ui_bridge',
      severity: u.snapshotStale ? 'warn' : 'pass',
      observed: u.snapshotStale,
      threshold: 'false',
      basis: 'stale snapshot is an observation-path issue, flagged separately',
      message: u.snapshotStale ? 'snapshot stale' : 'snapshot fresh',
    },
    {
      id: 'ui.consumer_lag',
      module: 'ui_bridge',
      severity: ascending(
        u.lastJsConsumerLagMs,
        uiConsumerLagMs.warn,
        uiConsumerLagMs.fail,
      ),
      observed: u.lastJsConsumerLagMs,
      threshold: `< ${uiConsumerLagMs.warn}ms`,
      basis: 'rising consumer lag indicates a UI-thread bottleneck',
      message: `consumer lag ${u.lastJsConsumerLagMs ?? '—'}ms`,
    },
  ];
};

const buildDegradation = (s: DiagnosticsSnapshot): Check[] => {
  const d = s.degradation;
  const { degradationLevel } = DIAGNOSTICS_THRESHOLDS;
  const actionSeverity: DiagnosticSeverity =
    d.action === 'low_performance_mode'
      ? 'fail'
      : d.action === 'none'
        ? 'pass'
        : 'warn';
  return [
    {
      id: 'degradation.level',
      module: 'degradation',
      severity: ascending(
        d.level,
        degradationLevel.warn,
        degradationLevel.fail,
      ),
      observed: d.level,
      threshold: '0',
      basis: 'sustained degradation level reflects realtime quality loss',
      message: `level ${d.level} (${d.reason})`,
    },
    {
      id: 'degradation.action',
      module: 'degradation',
      severity: actionSeverity,
      observed: d.action,
      threshold: 'none',
      basis: 'an active mitigation action affects user experience',
      message: `action ${d.action}`,
    },
  ];
};

const THERMAL_SEVERITY: Record<ThermalState, DiagnosticSeverity> = {
  nominal: 'pass',
  fair: 'pass',
  serious: 'warn',
  critical: 'fail',
  unknown: 'pass',
};

const buildPlatform = (s: DiagnosticsSnapshot): Check[] => {
  const p = s.platform;
  const route = p.audioRoute;
  const routeSeverity: DiagnosticSeverity =
    route === 'bluetooth' ? 'warn' : route === 'unknown' ? 'fail' : 'pass';
  return [
    {
      id: 'platform.thermal',
      module: 'platform',
      severity: THERMAL_SEVERITY[p.thermalState ?? 'unknown'],
      observed: p.thermalState ?? null,
      threshold: 'nominal/fair',
      basis: 'sustained thermal pressure degrades performance',
      message: `thermal ${p.thermalState ?? '—'}`,
    },
    {
      id: 'platform.audio_route',
      module: 'platform',
      severity: routeSeverity,
      observed: route ?? null,
      threshold: 'speaker/wired',
      basis: 'route affects latency and user feedback alignment',
      message: `route ${route ?? '—'}`,
    },
    {
      id: 'platform.input_latency',
      module: 'platform',
      severity: p.estimatedInputLatencyMs == null ? 'warn' : 'pass',
      observed: p.estimatedInputLatencyMs ?? null,
      threshold: 'measured',
      basis: 'unmeasured input latency breaks scoring time alignment',
      message: `input latency ${p.estimatedInputLatencyMs ?? '—'}ms`,
    },
  ];
};

const MODULE_BUILDERS: Array<(s: DiagnosticsSnapshot) => Check[]> = [
  buildSession,
  buildAudioInput,
  buildRingBuffer,
  buildAnalysis,
  buildDetector,
  buildPostProcessor,
  buildEventDispatcher,
  buildUiBridge,
  buildDegradation,
  buildPlatform,
];

// ──────────────────────────────────────────────────────────────────────────
// Orchestrator
// ──────────────────────────────────────────────────────────────────────────

/**
 * Validate a diagnostics snapshot into a full report. Pure and deterministic:
 * pass `generatedAtMs` to stamp the report, otherwise the snapshot timestamp is
 * reused so identical input yields identical output.
 */
export const validateDiagnostics = (
  snapshot: DiagnosticsSnapshot,
  options: ValidateDiagnosticsOptions = {},
): DiagnosticsValidationReport => {
  const timestampMs = snapshot.timestampMs;
  const checks: DiagnosticCheckResult[] = MODULE_BUILDERS.flatMap((build) =>
    build(snapshot).map((check) => ({ ...check, timestampMs })),
  );
  return {
    schemaVersion: 1,
    thresholdVersion: THRESHOLD_VERSION,
    generatedAtMs: options.generatedAtMs ?? timestampMs,
    sessionId: snapshot.session.sessionId,
    device: options.device,
    snapshotTimestampMs: timestampMs,
    overall: worst(checks.map((c) => c.severity)),
    checks,
  };
};
